import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Mail, Phone, MapPin, StickyNote, Send, Link2, ShieldAlert, Pencil, Plus, Wrench, Power, CalendarPlus, Briefcase, ChevronRight } from "lucide-react";
import EmptyState from "../components/EmptyState";
import { Sheet } from "../components/PartnerForm";
import { Avatar, Button, Field, Spinner } from "../components/ui/kit";
import { useAuth } from "../context/AuthContext";
import { canSee, canEditAktivita, jeSubdodavatel, ROLE_LABEL } from "../lib/nav";
import { fmtDate, fmtDateTime } from "../lib/format";
import api, { apiError } from "../lib/api";
import NahratObrazek from "../components/NahratObrazek";
import { Drobecky, useNazevStranky, useUrlStav } from "../lib/navigace";
import { useJeMobil } from "../lib/mobil";
import { SkupinyAktivit } from "../lib/aktivity";
import { FazePill, etapa } from "../lib/faze";
import DetailHlava, { KartaZalozky, KartaSekce, Pridat, StitekTmavy } from "../components/DetailHlava";
import RadekAktivity from "../components/RadekAktivity";
import AktivitaDetail from "../components/AktivitaDetail";
import NovaAktivita from "../components/NovaAktivita";
import { OdkazPozvanky, stavClena } from "./Team";
import { useSpecializace, MA_SPECIALIZACI, KartaSpecializace, KartaRozsah, KartaPristupKlientu } from "../components/SpecializaceClena";
import { zkratkaTechnologie } from "../lib/technologie";

const ROLE = ["OWNER", "OBCHODNIK", "TECHNIK", "ASISTENTKA", "SUBDODAVATEL"];

function Radek({ icon: Icon, label, value, href }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-line-softer last:border-0">
      <Icon size={16} strokeWidth={1.7} className="text-muted mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="label-up text-muted">{label}</div>
        {value
          ? href ? <a href={href} className="t-telo text-ink hover:underline break-words">{value}</a>
                 : <div className="t-telo text-ink break-words">{value}</div>
          : <div className="t-telo text-muted-2">—</div>}
      </div>
    </div>
  );
}

function UpravaClena({ m, smiRole, onClose, onSaved }) {
  const pozvany = m.stav === "POZVANY";
  const [f, setF] = useState({
    name: m.name || "", email: m.email || "", phone: m.phone || "", pozice: m.pozice || "",
    adresa: m.adresa || "", poznamka: m.poznamka || "",
  });
  const [foto, setFoto] = useState(m.photo_url || "");
  const [roles, setRoles] = useState(m.roles || []);
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pick = (r) => {
    if (r === "SUBDODAVATEL") return setRoles(["SUBDODAVATEL"]);
    setRoles((prev) => {
      const base = prev.filter((x) => x !== "SUBDODAVATEL");
      return base.includes(r) ? base.filter((x) => x !== r) : [...base, r];
    });
  };

  const uloz = async (e) => {
    e.preventDefault();
    setBusy(true); setChyba("");
    try {
      const body = { name: f.name, phone: f.phone, pozice: f.pozice, adresa: f.adresa, poznamka: f.poznamka };
      if (foto !== (m.photo_url || "")) body.photo_url = foto;   // "" = odebrat
      if (pozvany) body.email = f.email;
      if (smiRole) body.roles = roles;
      await api.patch(`/api/team/${m.id}`, body);
      onSaved();
      onClose();
    } catch (err) {
      setChyba(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <Sheet titulek="Upravit údaje" onClose={onClose} testid="clen-uprava">
      <form onSubmit={uloz} className="p-6 space-y-4">
        <NahratObrazek tvar="foto" jmeno={f.name} value={foto} onChange={setFoto} testid="clen-foto" />
        <Field label="Jméno a příjmení">
          <input className="field" value={f.name} onChange={upd("name")} required minLength={2} data-testid="clen-name" />
        </Field>
        {pozvany ? (
          <Field label="E-mail" hint="Po změně přestane platit dosavadní odkaz na pozvánku.">
            <input type="email" className="field" value={f.email} onChange={upd("email")} required data-testid="clen-email" />
          </Field>
        ) : (
          <div>
            <span className="label-up text-muted block mb-1.5">E-mail</span>
            <div className="field flex items-center text-muted bg-line-softer cursor-not-allowed">{m.email}</div>
            <span className="block mt-1 text-[11px] text-muted-2">Přihlašovací e-mail si člen mění sám v Nastavení.</span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Telefon"><input className="field" value={f.phone} onChange={upd("phone")} inputMode="tel" data-testid="clen-phone" /></Field>
          <Field label="Pozice"><input className="field" value={f.pozice} onChange={upd("pozice")} placeholder="Servisní technik" data-testid="clen-pozice" /></Field>
        </div>
        <Field label="Adresa"><input className="field" value={f.adresa} onChange={upd("adresa")} data-testid="clen-adresa" /></Field>
        <Field label="Interní poznámka" hint="Vidí jen majitel.">
          <textarea className="field min-h-[88px] py-2.5" value={f.poznamka} onChange={upd("poznamka")} data-testid="clen-poznamka" />
        </Field>
        {smiRole && (
          <div>
            <span className="label-up text-muted block mb-2">Role</span>
            <div className="flex flex-wrap gap-2">
              {ROLE.map((r) => (
                <button type="button" key={r} onClick={() => pick(r)}
                  className={`tap md:min-h-0 pill px-3 h-8 text-[12px] font-600 border ${roles.includes(r) ? "bg-accent text-white border-accent" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
                  {ROLE_LABEL[r]}
                </button>
              ))}
            </div>
            {roles.includes("OWNER") && !(m.roles || []).includes("OWNER") && (
              <p className="t-meta text-muted mt-2">Majitel má plná práva: ekonomika, ceny, nastavení firmy i správa týmu.</p>
            )}
          </div>
        )}
        {chyba && <div className="t-meta text-danger" data-testid="clen-chyba">{chyba}</div>}
        <Button className="tap w-full md:w-auto justify-center" disabled={busy || (smiRole && !roles.length)} data-testid="clen-ulozit">
          {busy ? <Spinner /> : "Uložit"}
        </Button>
      </form>
    </Sheet>
  );
}

function SmazatClena({ m, onClose, onDone }) {
  const [potvrzeni, setPotvrzeni] = useState("");
  const [chyba, setChyba] = useState("");
  const [busy, setBusy] = useState(false);
  const shoda = potvrzeni.trim() === (m.name || "").trim();

  const smazat = async (e) => {
    e.preventDefault();
    setBusy(true); setChyba("");
    try {
      await api.delete(`/api/team/${m.id}`, { data: { confirm: potvrzeni } });
      onDone();
    } catch (err) {
      setChyba(apiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <Sheet titulek="Smazat člena natrvalo" onClose={onClose} testid="clen-smazat">
      <form onSubmit={smazat} className="p-6 space-y-4">
        <p className="t-telo text-sec">
          Smaže účet <b className="text-ink">{m.name}</b> včetně kontaktů a přihlášení. Z otevřených aktivit
          a obchodních případů ho odebereme. Historie (co založil, uzavřené aktivity, servisní zápisy) zůstane.
          E-mail půjde znovu použít.
        </p>
        <p className="t-meta text-muted">Když ho chceš jen dočasně odstavit, použij radši Deaktivovat.</p>
        <Field label={`Pro potvrzení napiš jméno: ${m.name}`}>
          <input className="field" value={potvrzeni} onChange={(e) => setPotvrzeni(e.target.value)}
            autoComplete="off" data-testid="clen-smazat-jmeno" />
        </Field>
        {chyba && <div className="t-meta text-danger">{chyba}</div>}
        <button type="submit" disabled={!shoda || busy} data-testid="clen-smazat-potvrdit"
          className="tap w-full md:w-auto pill px-5 h-11 md:h-10 font-700 t-telo bg-danger text-white disabled:opacity-40 flex items-center justify-center">
          {busy ? <Spinner /> : "Smazat natrvalo"}
        </button>
      </form>
    </Sheet>
  );
}

export default function TeamMemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isOwner = canSee(user, ["OWNER"]);
  const mobil = useJeMobil();
  const [m, setM] = useState(undefined);
  const [dialog, setDialog] = useState(null);     // "uprava" | "smazat"
  const [odkaz, setOdkaz] = useState("");
  const [zprava, setZprava] = useState("");
  const [busy, setBusy] = useState("");

  const [tab, setTab] = useUrlStav("tab", "aktivity");
  const [acts, setActs] = useState(null);
  const [cases, setCases] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [novaAkt, setNovaAkt] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/team/${id}`); setM(data); }
    catch { setM(null); }
  }, [id]);
  // Co má člověk na starosti: jeho aktivity a případy, které vede jako obchodník.
  const loadActs = useCallback(() => {
    api.get("/api/aktivity", { params: { odpovedna_osoba: id, limit: 200 } })
      .then(({ data }) => setActs(data.items || [])).catch(() => setActs([]));
  }, [id]);
  const loadCases = useCallback(() => {
    api.get("/api/obchodni-pripady", { params: { obchodnik_id: id, limit: 100 } })
      .then(({ data }) => setCases(data.items || [])).catch(() => setCases([]));
  }, [id]);
  useEffect(() => { load(); loadActs(); }, [load, loadActs]);
  useEffect(() => { if (tab === "pripady") loadCases(); }, [tab, loadCases]);
  const toggleAkt = async (a) => { await api.post(`/api/aktivity/${a.id}/hotovo`); loadActs(); load(); };
  useNazevStranky(m?.name);
  const spec = useSpecializace(m, load, isOwner);

  const pozvanka = async (odeslat) => {
    setBusy(odeslat ? "poslat" : "odkaz"); setZprava("");
    try {
      const { data } = await api.post(`/api/team/${id}/pozvanka?odeslat=${odeslat}`);
      setOdkaz(data.token_dev);
      setZprava(odeslat ? `Pozvánka odešla na ${m.email}.` : "Nový odkaz je připravený, starý přestal platit.");
      load();
    } catch (err) { setZprava(apiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(""); }
  };
  const prepnoutAktivitu = async () => {
    setBusy("stav"); setZprava("");
    try { await api.patch(`/api/team/${id}/status?active=${!m.active}`); load(); }
    catch (err) { setZprava(apiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(""); }
  };

  if (m === undefined) return <div className="p-10 flex justify-center text-muted"><Spinner /></div>;
  if (m === null)
    return <div className="card p-8"><EmptyState icon="Users" title="Člen nenalezen"
      description="Člen neexistuje nebo byl smazán."
      action={<button onClick={() => nav("/tym")} className="btn-ghost">Zpět na tým</button>} /></div>;

  const ja = m.id === user?.id;
  const pozvany = m.stav === "POZVANY";
  const sub = (m.roles || []).includes("SUBDODAVATEL");
  const s = m.stats || {};
  const btn = "tap md:min-h-0 pill border border-line-strong bg-white px-3.5 h-10 md:h-9 t-meta font-600 text-ink hover:bg-inputbg flex items-center justify-center gap-1.5";
  const stav = stavClena(m);
  const smiPlanovat = !jeSubdodavatel(user);
  const tel = m.phone ? m.phone.replace(/\s/g, "") : null;
  const specKarty = isOwner && MA_SPECIALIZACI(m.roles);
  const umi = specKarty && spec.nacteno && spec.spec.length
    ? spec.technologie.filter((t) => spec.spec.includes(t.id)).map(zkratkaTechnologie).join(" · ") : null;

  return (
    <div data-testid="clen-detail" className="flex flex-col gap-4">
      <Drobecky cesta={[{ nazev: "Tým", to: "/tym", testid: "clen-zpet" }]} aktualni={m.name} />

      <DetailHlava testid="clen-hlava" druh="Člen týmu" nazev={m.name}
        obrazek={<span className="block rounded-full ring-[3px] ring-white/10"><Avatar name={m.name} url={m.photo_url} size={mobil ? 48 : 60} /></span>}
        stitky={<>
          {(m.roles || []).map((r) => (
            <StitekTmavy key={r} barva={r === "OWNER" ? "#ffb182" : "#d7dade"}>{ROLE_LABEL[r] || r}</StitekTmavy>
          ))}
          {m.pozice && <StitekTmavy>{m.pozice}</StitekTmavy>}
          <StitekTmavy testid="clen-stav" barva={!m.active ? "#ff8a7a" : stav ? "#f0b551" : "#5fd3a5"}>{stav ? stav.label : "Aktivní"}</StitekTmavy>
        </>}
        meta={[
          m.email && <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-white"><Mail size={13} />{m.email}</a>,
          m.phone && <a href={`tel:${tel}`} className="amount inline-flex items-center gap-1.5 hover:text-white"><Phone size={13} />{m.phone}</a>,
          m.adresa && <span className="inline-flex items-center gap-1.5"><MapPin size={13} />{m.adresa}</span>,
          umi && <span className="inline-flex items-center gap-1.5" data-testid="clen-umi"><Wrench size={13} />Umí {umi}</span>,
        ].filter(Boolean)}
        vedlejsi={isOwner ? [{ text: "Upravit", ikona: Pencil, onClick: () => setDialog("uprava"), testid: "clen-upravit" }] : []}
        hlavni={smiPlanovat ? { text: "Naplánovat aktivitu", ikona: Plus, onClick: () => setNovaAkt(true), testid: "clen-nova-aktivita" } : null}
        vice={[
          ...(isOwner ? [{ text: "Upravit údaje a role", ikona: Pencil, onClick: () => setDialog("uprava"), testid: "clen-vice-upravit" }] : []),
          ...(isOwner && !ja ? [{ text: m.active ? "Deaktivovat" : "Aktivovat", ikona: Power, onClick: prepnoutAktivitu, testid: "clen-aktivita" }] : []),
          ...(isOwner && !ja ? [{ text: "Smazat natrvalo", ikona: ShieldAlert, nebezpecne: true, onClick: () => setDialog("smazat"), testid: "clen-smazat-otevrit" }] : []),
        ]}
        cta={[
          ...(smiPlanovat ? [{ text: "Aktivita", ikona: CalendarPlus, onClick: () => setNovaAkt(true), testid: "clen-cta-aktivita" }] : []),
          ...(tel ? [{ text: "Zavolat", ikona: Phone, href: `tel:${tel}`, testid: "clen-cta-zavolat" }] : []),
          ...(m.email ? [{ text: "E-mail", ikona: Mail, href: `mailto:${m.email}`, testid: "clen-cta-email" }] : []),
        ]}
        dlazdice={[
          { popisek: "Otevřené aktivity", hodnota: String(s.aktivity_otevrene ?? 0), testid: "clen-aktivity",
            pod: acts ? `${acts.filter((a) => a.stav === "HOTOVA").length} hotových` : null },
          ...(!sub ? [{ popisek: "Obchodní případy", hodnota: String(s.pripady ?? 0), pod: "jako obchodník", testid: "clen-pripady" }] : []),
          { popisek: "V týmu od", hodnota: fmtDate(m.created_at), testid: "clen-od" },
          { popisek: "Poslední přihlášení", hodnota: m.last_login_at ? fmtDateTime(m.last_login_at) : pozvany ? "zatím ne" : "—", testid: "clen-prihlaseni" },
        ]} />

      {isOwner && pozvany && (
        <div className="card p-5 md:px-[22px]" data-testid="clen-pozvanka">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
            <div className="flex-1 min-w-0">
              <h2 className="t-sekce font-700 text-ink">
                {m.pozvanka_odeslana_at ? "Čeká na přijetí pozvánky" : "Zatím nedostal pozvánku"}
              </h2>
              <p className="t-meta text-muted mt-1">
                {m.pozvanka_odeslana_at
                  ? `Pozvánka odešla ${fmtDateTime(m.pozvanka_odeslana_at)}${m.pozvanka_plati_do ? `, odkaz platí do ${fmtDate(m.pozvanka_plati_do)}` : ""}.`
                  : "Je v týmu a jde mu přiřazovat práci, jen se zatím nemůže přihlásit."}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <button onClick={() => pozvanka(false)} disabled={!!busy} className={btn} data-testid="clen-odkaz">
                {busy === "odkaz" ? <Spinner /> : <><Link2 size={14} /> Odkaz k předání</>}
              </button>
              <Button className="tap md:min-h-0 justify-center" onClick={() => pozvanka(true)} disabled={!!busy} data-testid="clen-poslat-pozvanku">
                {busy === "poslat" ? <Spinner /> : <><Send size={14} /> {m.pozvanka_odeslana_at ? "Poslat znovu" : "Poslat pozvánku"}</>}
              </Button>
            </div>
          </div>
          {odkaz && <div className="mt-4"><OdkazPozvanky token={odkaz} /></div>}
        </div>
      )}
      {zprava && <div className="t-meta text-sec" data-testid="clen-zprava">{zprava}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
        <div className="flex flex-col gap-4 min-w-0">
        <KartaZalozky testid="clen-zalozky" aktivni={sub ? "aktivity" : tab} onZmena={setTab}
          akce={tab === "aktivity" && smiPlanovat && <Pridat text="Aktivita" onClick={() => setNovaAkt(true)} testid="clen-pridat-aktivitu" />}
          zalozky={[
            { key: "aktivity", label: "Aktivity", pocet: acts ? acts.length : null, testid: "clen-tab-aktivity" },
            ...(!sub ? [{ key: "pripady", label: "Obchodní případy", pocet: s.pripady ?? null, testid: "clen-tab-pripady" }] : []),
          ]}>
          {(sub || tab === "aktivity") && (
            acts === null ? <div className="p-8 flex justify-center text-muted"><Spinner /></div>
              : acts.length === 0 ? (
                <EmptyState icon="CalendarCheck" title="Nic na starosti" description="Až mu přiřadíš aktivitu, objeví se tady." />
              ) : <SkupinyAktivit items={acts} render={(a) => (
                <RadekAktivity key={a.id} a={a} onOpen={() => setDetailId(a.id)}
                  onToggle={canEditAktivita(user, a.oblast) ? () => toggleAkt(a) : null} />
              )} />
          )}
          {!sub && tab === "pripady" && isOwner && cases?.length > 0 && (
            <PredatPripady clenId={id} pocet={s.pripady ?? cases.length} onHotovo={() => { load(); loadCases(); loadActs(); }} />
          )}
          {!sub && tab === "pripady" && (
            cases === null ? <div className="p-8 flex justify-center text-muted"><Spinner /></div>
              : cases.length === 0 ? (
                <EmptyState icon="Briefcase" title="Žádné obchodní případy" description="Případy, které vede jako obchodník, se tu objeví samy." />
              ) : cases.map((c) => (
                <button key={c.id} onClick={() => nav(`/obchod/${c.id}`)} data-testid={`clen-pripad-${c.id}`}
                  className="w-full text-left flex items-center gap-3.5 px-4 md:px-5 py-3 border-b border-line-soft last:border-0 hover:bg-inputbg">
                  <span className="h-9 w-9 rounded-[12px] bg-inputbg text-sec inline-flex items-center justify-center shrink-0"><Briefcase size={16} strokeWidth={1.9} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block t-telo font-600 text-ink truncate">{c.nazev}</span>
                    <span className="block t-meta text-muted truncate"><span className="amount">{c.cislo}</span>{c.client_name ? ` · ${c.client_name}` : ""}</span>
                  </span>
                  <FazePill faze={etapa(c)} />
                  <ChevronRight size={15} className="text-muted-2 shrink-0" />
                </button>
              ))
          )}
        </KartaZalozky>
          {/* Telefon: specializace a rozsah nad seznamem klientů; počítač je má v pravém sloupci. */}
          {specKarty && sub && (
            <div className="flex flex-col gap-4 lg:hidden">
              <KartaSpecializace s={spec} />
              <KartaRozsah s={spec} />
            </div>
          )}
          {specKarty && sub && <KartaPristupKlientu s={spec} />}
        </div>

        <aside className="flex flex-col gap-4 min-w-0">
          {specKarty && !sub && <KartaSpecializace s={spec} />}
          {specKarty && sub && (
            <div className="hidden lg:flex flex-col gap-4">
              <KartaSpecializace s={spec} />
              <KartaRozsah s={spec} />
            </div>
          )}
          <KartaSekce titul="Kontakty" telo>
            <Radek icon={Mail} label="E-mail" value={m.email} href={m.email ? `mailto:${m.email}` : null} />
            <Radek icon={Phone} label="Telefon" value={m.phone} href={tel ? `tel:${tel}` : null} />
            <Radek icon={MapPin} label="Adresa" value={m.adresa} />
          </KartaSekce>
          {isOwner && (
            <KartaSekce titul="Interní poznámka" telo akce={<Pridat text="Upravit" onClick={() => setDialog("uprava")} testid="clen-poznamka-upravit" />}>
              {m.poznamka
                ? <p className="t-telo md:text-[13.5px] text-ink whitespace-pre-wrap" data-testid="clen-poznamka-text">{m.poznamka}</p>
                : <p className="t-meta text-muted"><StickyNote size={12} className="inline -mt-0.5 mr-1" />Vidí jen majitel. Zatím prázdná.</p>}
            </KartaSekce>
          )}
        </aside>
      </div>

      {novaAkt && <NovaAktivita defaultOsoby={[m.id]} onClose={() => setNovaAkt(false)} onSaved={() => { setNovaAkt(false); loadActs(); load(); }} />}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(x) => { setDetailId(null); setEditAct(x); }}
          onChanged={() => { loadActs(); load(); }} />
      )}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); loadActs(); load(); }} />
      )}
      {dialog === "uprava" && <UpravaClena m={m} smiRole={isOwner && !ja} onClose={() => setDialog(null)} onSaved={load} />}
      {dialog === "smazat" && <SmazatClena m={m} onClose={() => setDialog(null)} onDone={() => nav("/tym")} />}
    </div>
  );
}


// Majitel předá všechny případy člena jinému (po převodu dat, odchodu z firmy) — i s otevřenými úkoly.
function PredatPripady({ clenId, pocet, onHotovo }) {
  const [tym, setTym] = useState([]);
  const [komu, setKomu] = useState("");
  const [busy, setBusy] = useState(false);
  const [zprava, setZprava] = useState("");
  useEffect(() => {
    api.get("/api/team").then((r) => setTym((r.data.members || [])
      .filter((m) => m.active && m.id !== clenId && !(m.roles || []).includes("SUBDODAVATEL")))).catch(() => {});
  }, [clenId]);
  const predat = async () => {
    const jmeno = tym.find((m) => m.id === komu)?.name || "";
    if (!window.confirm(`Předat ${pocet} případů ${jmeno}? Otevřené úkoly z postupu půjdou s nimi.`)) return;
    setBusy(true); setZprava("");
    try {
      const { data } = await api.post(`/api/team/${clenId}/predat-pripady`, { komu });
      setZprava(`Předáno ${data.pripadu} případů a ${data.ukolu} úkolů.`);
      setKomu("");
      onHotovo();
    } catch (e) { setZprava(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="px-4 md:px-5 py-3 border-b border-line-soft flex flex-col md:flex-row md:items-center gap-2 md:gap-3" data-testid="predat-pripady">
      <span className="t-meta text-muted md:flex-1">Předat všechny případy (i s jejich otevřenými úkoly) jinému obchodníkovi</span>
      <select className="field w-full md:w-56 min-w-0" value={komu} onChange={(e) => setKomu(e.target.value)}
        aria-label="Komu předat případy" data-testid="predat-pripady-komu">
        <option value="">Komu…</option>
        {tym.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
      <Button onClick={predat} disabled={!komu || busy} className="w-full md:w-auto" data-testid="predat-pripady-potvrdit">
        {busy ? <Spinner /> : "Předat"}
      </Button>
      {zprava && <span className="t-meta text-sec md:basis-full" data-testid="predat-pripady-zprava">{zprava}</span>}
    </div>
  );
}
